import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { api, type DraftStatus, type SectionDraftDetail } from '../api/client'
import { describeError } from '../lib/describeError'
import { usePublicBaseUrl } from '../lib/publicBaseUrls'
import { addPending, alreadyListed, canSaveReview, hitTitle, refKey, searchTerm, sourceHref, sourceEdit, truncate, writeStillPending, type ArchiveHit, type TitledRef } from '../lib/reviewSources'

const CHIP = 'text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-ink-700 text-ink-200'
const INPUT = 'bg-ink-900 border border-ink-600 text-paper text-[13px] p-1.5'

/** Search this title's member archive and magazine issues. Debounced 300 ms;
 *  runs from 2 characters. Each hit gets a Use button and, when `onBlock` is
 *  given, a Don't use button. */
export function ArchiveSearch({ publicationSlug, onUse, onBlock, useLabel = 'Use', blockLabel = "Don't use", listed }: {
  publicationSlug: string; onUse: (h: ArchiveHit) => void; onBlock?: (h: ArchiveHit) => void; useLabel?: string; blockLabel?: string
  /** Hits already in the caller's list get a disabled "In the list" button. */
  listed?: (h: ArchiveHit) => boolean
}) {
  const [text, setText] = useState('')
  const [term, setTerm] = useState<string | null>(null)
  useEffect(() => {
    const t = setTimeout(() => setTerm(searchTerm(text)), 300)
    return () => clearTimeout(t)
  }, [text])
  const q = useQuery({
    queryKey: ['section-archive-search', publicationSlug, term],
    queryFn: () => api.get<{ items: ArchiveHit[] }>(`/api/lab/section-drafts/archive-search?publication_slug=${encodeURIComponent(publicationSlug)}&q=${encodeURIComponent(term ?? '')}`),
    enabled: term !== null,
    staleTime: 60_000,
  })
  return (
    <div className="space-y-1">
      {/* Enter must not submit the drafting form this sits in. */}
      <input type="search" value={text} onChange={(e) => setText(e.target.value)} maxLength={100}
             onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}
             placeholder="Search member articles and issues" className={`w-full ${INPUT}`} />
      {q.isError && <p className="text-red-300 text-[12px]">{describeError(q.error)}</p>}
      {term !== null && q.data && q.data.items.length === 0 && <p className="label">Nothing in this title's archive matches.</p>}
      {term !== null && !!q.data?.items.length && (
        <ul className="max-h-56 overflow-y-auto divide-y divide-ink-700 border border-ink-700 rounded">
          {q.data.items.map((h) => (
            <li key={`${h.kind}:${h.id}`} className="flex items-center gap-2 px-2 py-1 text-[13px]">
              <span className={CHIP}>{h.kind === 'issue' ? 'issue' : 'member'}</span>
              <span className="text-paper flex-1 min-w-0 truncate" title={hitTitle(h)}>{hitTitle(h)}</span>
              {h.label && <span className="label">{h.label}</span>}
              {listed?.(h)
                ? <Button type="button" size="sm" variant="outline" disabled>In the list</Button>
                : <Button type="button" size="sm" variant="outline" onClick={() => onUse(h)}>{useLabel}</Button>}
              {onBlock && <Button type="button" size="sm" variant="ghost" onClick={() => onBlock(h)}>{blockLabel}</Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** A draft that stopped after gathering sources (status sources_ready):
 *  the editor removes or adds sources, saves, then presses Write it. */
export function ReviewSources({ draft: d, running, onChange }: { draft: SectionDraftDetail; running: boolean; onChange: () => void }) {
  const publicBase = usePublicBaseUrl(d.publication_slug)
  const qc = useQueryClient()
  const [remove, setRemove] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<TitledRef[]>([])
  const dirty = remove.size > 0 || pending.length > 0
  const save = useMutation({
    mutationFn: () => api.patch<SectionDraftDetail>(`/api/lab/section-drafts/${d.id}/sources`, sourceEdit(remove, pending)),
    onSuccess: () => { setRemove(new Set()); setPending([]); onChange() },
  })
  // After a successful POST the button stays off until the status poll shows
  // a run finished after it (ok or not), or 90 s pass with nothing running.
  // Same query the page polls; this observer only reads the shared cache.
  const status = useQuery({
    queryKey: ['section-draft-status', d.publication_slug],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${d.publication_slug}`),
  })
  const [postedAt, setPostedAt] = useState<number | null>(null)
  const [now, setNow] = useState(0)
  useEffect(() => {
    if (postedAt === null) return
    const t = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(t)
  }, [postedAt])
  const started = writeStillPending(postedAt, status.data, Math.max(now, postedAt ?? 0))
  const write = useMutation({
    mutationFn: () => api.post<{ started: boolean }>(`/api/lab/section-drafts/${d.id}/write`),
    onSuccess: () => {
      const t = Date.now()
      setPostedAt(t)
      setNow(t)
      qc.invalidateQueries({ queryKey: ['section-draft-status', d.publication_slug] })
      onChange()
    },
  })
  const toggle = (id: string) => setRemove((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    return n
  })
  const questions = d.research_questions ?? []
  const attention = d.verification.attention ?? []
  return (
    <div className="space-y-4">
      <p className="label normal-case tracking-normal">
        These are the sources the writer will use. Remove any that are off the subject, add articles or issues it
        missed, save, then press Write it.
      </p>
      {questions.length > 0 && (
        <div>
          <span className="label">Research questions</span>
          <ol className="list-decimal ml-5 mt-1 space-y-0.5 text-[13px] text-paper">
            {questions.map((rq, i) => <li key={i}>{rq}</li>)}
          </ol>
        </div>
      )}
      {attention.length > 0 && (
        <ul className="text-amber-200 text-[13px] space-y-0.5">
          {attention.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}
      <div>
        <span className="label">Sources ({d.sources.length})</span>
        <ul className="mt-1 divide-y divide-ink-700 border border-ink-700 rounded">
          {d.sources.map((s) => {
            const href = sourceHref(publicBase, s)
            const first = s.passages[0]
            const gone = remove.has(s.id)
            return (
              <li key={s.id} className={`px-2 py-2 text-[13px] ${gone ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className={CHIP}>{s.kind}</span>
                  {href
                    ? <a className={`text-paper underline flex-1 min-w-0 ${gone ? 'line-through' : ''}`} href={href} target="_blank" rel="noreferrer">{s.title}</a>
                    : <span className={`text-paper flex-1 min-w-0 ${gone ? 'line-through' : ''}`}>{s.title}</span>}
                  {s.reference_only && <span className={CHIP}>reference only</span>}
                  <label className="label flex items-center gap-1 whitespace-nowrap">
                    <input type="checkbox" checked={gone} onChange={() => toggle(s.id)} /> Remove
                  </label>
                </div>
                {first && <p className="text-ink-200 mt-1">{truncate(first, 300)}</p>}
              </li>
            )
          })}
          {d.sources.length === 0 && <li className="px-2 py-2 label">No sources were found. Add some below.</li>}
        </ul>
      </div>
      <div>
        <span className="label">Add from the archive</span>
        <div className="mt-1">
          <ArchiveSearch publicationSlug={d.publication_slug} useLabel="Add"
                         listed={(h) => alreadyListed(h, d.sources.map((s) => s.id), remove, pending)} onUse={(h) => setPending((p) => addPending(p, h))} />
        </div>
        {pending.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {pending.map((p) => (
              <li key={refKey(p.ref)} className="flex items-center gap-1 border border-emerald-700 rounded px-2 py-0.5 text-[12px] text-emerald-200">
                <span className="uppercase text-[10px]">add {p.ref.kind}</span> {p.title}
                <button type="button" aria-label={`Do not add ${p.title}`} className="ml-1"
                        onClick={() => setPending((xs) => xs.filter((x) => refKey(x.ref) !== refKey(p.ref)))}>×</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {d.error && <p className="text-red-300 text-[13px]">The last attempt failed: {d.error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {/* No edits while the writer is reading this bundle. */}
        <Button variant="outline" disabled={!canSaveReview({ dirty, saving: save.isPending, running, started, writing: write.isPending })} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button disabled={running || started || dirty || write.isPending} onClick={() => write.mutate()}>
          {running || started ? 'A draft is being written…' : write.isPending ? 'Starting…' : 'Write it'}
        </Button>
        {dirty && <span className="label">Save your changes before writing.</span>}
      </div>
      {save.isError && <p className="text-red-300 text-[13px]">{describeError(save.error)}</p>}
      {write.isError && <p className="text-red-300 text-[13px]">{describeError(write.error)}</p>}
    </div>
  )
}
